import { contentService } from "@/services";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { qk } from "./queryKeys";

/**
 * The document library.
 *
 * Polls while anything is still ingesting. Extraction and embedding happen out
 * of band and nothing pushes a completion to this tab, so without polling an
 * admin watches "extracting" forever and reloads to find out it finished
 * minutes ago. Polling STOPS once everything settles, so an idle library costs
 * nothing.
 */
export function useContent() {
	return useQuery({
		queryKey: qk.content,
		queryFn: ({ signal }) => contentService.list(signal),
		refetchInterval: (q) => {
			const busy = q.state.data?.sources.some(
				(s) =>
					s.status === "pending" ||
					s.status === "extracting" ||
					s.status === "embedding",
			);
			return busy ? 3_000 : false;
		},
	});
}

export function useContentMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: qk.content });

	return {
		addUrl: useMutation({
			mutationFn: (body: { url: string; title?: string }) =>
				contentService.addUrl(body),
			onSuccess: invalidate,
		}),
		addText: useMutation({
			mutationFn: (body: { text: string; title?: string }) =>
				contentService.addText(body),
			onSuccess: invalidate,
		}),
		addFile: useMutation({
			mutationFn: (file: File) => contentService.addFile(file),
			onSuccess: invalidate,
		}),
		retry: useMutation({
			mutationFn: (id: string) => contentService.retry(id),
			onSuccess: invalidate,
		}),
		remove: useMutation({
			mutationFn: (id: string) => contentService.remove(id),
			onSuccess: invalidate,
		}),
	};
}
